import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useHelpArticles,
  useHelpCategories,
  useHelpLayout,
} from '@/presentation/hooks/useHelpDocumentation';
import { HelpLayoutEditor } from '@/presentation/components/help/HelpLayoutEditor';
import type {
  HelpCategoryDTO,
  HelpPageSectionDTO,
  HelpQuickActionDTO,
} from '@/domain/repositories/IHelpDocumentationRepository';

type Tab = 'categories' | 'layout';

const LOCALES = ['es', 'en', 'pt'];
const ICONS = ['calendar', 'patient', 'cash', 'note', 'lock', 'check'];
const RETIRED_SECTIONS = new Set(['quick_actions', 'faq', 'tips', 'announcements']);

function emptyLocales() {
  return LOCALES.map((locale) => ({ locale, name: '' }));
}

function moveItem<T>(list: T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const tmp = next[index];
  next[index] = next[target];
  next[target] = tmp;
  return next;
}

export const DocumentationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('layout');

  const {
    categories,
    isLoading: catsLoading,
    error: catsError,
    refetch: refetchCats,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useHelpCategories();

  const {
    articles: documentationArticles,
    isLoading: docsLoading,
    refetch: refetchDocs,
    deleteArticle,
  } = useHelpArticles({ contentType: 'article' });

  const {
    layout,
    isLoading: layoutLoading,
    error: layoutError,
    refetch: refetchLayout,
    saveLayout,
  } = useHelpLayout();

  const [sectionsDraft, setSectionsDraft] = useState<HelpPageSectionDTO[]>([]);
  const [actionsDraft, setActionsDraft] = useState<HelpQuickActionDTO[]>([]);
  const [expandedDocCategoryId, setExpandedDocCategoryId] = useState<string | null>(null);

  useEffect(() => {
    if (!layout) return;
    setSectionsDraft(
      layout.sections.map((s) => ({
        ...s,
        isVisible: RETIRED_SECTIONS.has(s.sectionKey) ? false : s.isVisible,
        locales: s.locales.map((l) => ({ ...l })),
      }))
    );
    setActionsDraft(
      layout.quickActions.map((a) => ({ ...a, locales: a.locales.map((l) => ({ ...l })) }))
    );
  }, [layout]);


  const [catModal, setCatModal] = useState<HelpCategoryDTO | 'new' | null>(null);
  const [catForm, setCatForm] = useState({
    slug: '',
    icon: 'calendar',
    sortOrder: 0,
    isActive: true,
    locales: emptyLocales(),
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) {
      const es = c.locales.find((l) => l.locale === 'es')?.name;
      map.set(c.id, es || c.slug);
    }
    return map;
  }, [categories]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sortOrder - b.sortOrder || a.slug.localeCompare(b.slug)),
    [categories]
  );

  const documentsByCategory = useMemo(() => {
    const map = new Map<
      string,
      Array<{
        slug: string;
        title: string;
        editId: string;
        ids: string[];
        sortOrder: number;
        publishedLocales: string[];
        locales: string[];
      }>
    >();

    const bySlug = new Map<string, {
      slug: string;
      categoryId: string | null;
      title: string;
      editId: string;
      ids: string[];
      sortOrder: number;
      publishedLocales: string[];
      locales: string[];
    }>();

    for (const art of documentationArticles) {
      if (art.contentType !== 'article') continue;
      const existing = bySlug.get(art.slug);
      if (!existing) {
        bySlug.set(art.slug, {
          slug: art.slug,
          categoryId: art.categoryId,
          title: art.title,
          editId: art.id,
          ids: [art.id],
          sortOrder: art.sortOrder,
          publishedLocales: art.isPublished ? [art.locale] : [],
          locales: [art.locale],
        });
      } else {
        existing.ids.push(art.id);
        existing.locales.push(art.locale);
        existing.sortOrder = Math.min(existing.sortOrder, art.sortOrder);
        if (art.isPublished) existing.publishedLocales.push(art.locale);
        if (art.locale === 'es') {
          existing.title = art.title;
          existing.editId = art.id;
        }
      }
    }

    for (const doc of bySlug.values()) {
      if (!doc.categoryId) continue;
      const list = map.get(doc.categoryId) ?? [];
      list.push(doc);
      map.set(doc.categoryId, list);
    }
    for (const [, list] of map) {
      list.sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title, 'es'));
    }
    return map;
  }, [documentationArticles]);

  const openNewCategory = () => {
    setCatForm({
      slug: '',
      icon: 'calendar',
      sortOrder: categories.length + 1,
      isActive: true,
      locales: emptyLocales(),
    });
    setCatModal('new');
  };

  const openEditCategory = (cat: HelpCategoryDTO) => {
    setCatForm({
      slug: cat.slug,
      icon: cat.icon || 'calendar',
      sortOrder: cat.sortOrder,
      isActive: cat.isActive,
      locales: LOCALES.map((locale) => ({
        locale,
        name: cat.locales.find((l) => l.locale === locale)?.name ?? '',
      })),
    });
    setCatModal(cat);
  };

  const handleSaveCategory = async () => {
    setSaving(true);
    try {
      const payload = {
        slug: catForm.slug.trim(),
        icon: catForm.icon,
        sortOrder: Number(catForm.sortOrder) || 0,
        isActive: catForm.isActive,
        locales: catForm.locales.filter((l) => l.name.trim()),
      };
      if (catModal === 'new') {
        await createCategory(payload);
      } else if (catModal) {
        await updateCategory(catModal.id, payload);
      }
      setCatModal(null);
      setToast(t('pages.documentation.saved'));
      await refetchCats();
    } catch (err) {
      setToast(err instanceof Error ? err.message : t('pages.documentation.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm(t('pages.documentation.confirmDeleteCategory'))) return;
    try {
      const result = await deleteCategory(id);
      setToast(
        result.deleted === 'soft'
          ? t('pages.documentation.categorySoftDeleted')
          : t('pages.documentation.deleted')
      );
      await refetchCats();
    } catch (err) {
      setToast(err instanceof Error ? err.message : t('pages.documentation.saveError'));
    }
  };

  const handleDeleteArticleGroup = async (ids: string[]) => {
    if (!window.confirm(t('pages.documentation.confirmDeleteArticle'))) return;
    try {
      for (const id of ids) {
        await deleteArticle(id);
      }
      setToast(t('pages.documentation.deleted'));
      await refetchDocs();
    } catch (err) {
      setToast(err instanceof Error ? err.message : t('pages.documentation.saveError'));
    }
  };

  const moveCategory = async (index: number, direction: -1 | 1) => {
    const next = moveItem(sortedCategories, index, direction);
    if (next === sortedCategories) return;
    try {
      await Promise.all(
        next.map((cat, i) => updateCategory(cat.id, { sortOrder: i + 1 }))
      );
      setToast(t('pages.documentation.orderUpdated'));
      await refetchCats();
    } catch (err) {
      setToast(err instanceof Error ? err.message : t('pages.documentation.saveError'));
    }
  };


  const handleSaveLayout = async () => {
    setSaving(true);
    try {
      await saveLayout({
        sections: sectionsDraft.map((s) => ({
          id: s.id,
          zone: s.zone,
          sortOrder: s.sortOrder,
          isVisible: RETIRED_SECTIONS.has(s.sectionKey) ? false : s.isVisible,
          locales: LOCALES.map((locale) => ({
            locale,
            title: s.locales.find((l) => l.locale === locale)?.title ?? '',
          })).filter((l) => l.title.trim()),
        })),
        quickActions: actionsDraft.map((a) => ({
          id: a.id,
          actionKey: a.actionKey,
          icon: a.icon,
          sortOrder: a.sortOrder,
          isActive: a.isActive,
          targetType: a.targetType || 'route',
          targetValue: a.targetValue || '',
          locales: LOCALES.map((locale) => ({
            locale,
            label: a.locales.find((l) => l.locale === locale)?.label ?? '',
          })).filter((l) => l.label.trim()),
        })),
      });
      setToast(t('pages.documentation.saved'));
      await refetchLayout();
    } catch (err) {
      setToast(err instanceof Error ? err.message : t('pages.documentation.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const refreshCurrent = () => {
    if (tab === 'categories') {
      void refetchCats();
      void refetchDocs();
    } else void refetchLayout();
  };

  return (
    <div className="panel-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="panel-page-title">
            {t('pages.documentation.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('pages.documentation.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={refreshCurrent}
          className="btn btn-sm btn-secondary"
        >
          {t('pages.documentation.refresh')}
        </button>
      </div>

      {toast && (
        <div className="rounded-md bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 px-4 py-2 text-sm flex justify-between">
          <span>{toast}</span>
          <button type="button" onClick={() => setToast(null)}>✕</button>
        </div>
      )}

      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {(['layout', 'categories'] as Tab[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 ${
              tab === key
                ? 'border-teal-600 text-teal-700 dark:text-teal-300'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            {t(`pages.documentation.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'layout' && (
        <HelpLayoutEditor
          sections={sectionsDraft}
          onSectionsChange={setSectionsDraft}
          onSave={() => void handleSaveLayout()}
          saving={saving}
          loading={layoutLoading}
          error={layoutError?.message ?? null}
        />
      )}

      {tab === 'categories' && (
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('pages.documentation.categoriesHint')}
            </p>
            <button
              type="button"
              onClick={openNewCategory}
              className="btn btn-sm btn-primary"
            >
              {t('pages.documentation.newCategory')}
            </button>
          </div>
          {catsLoading || docsLoading ? (
            <p className="text-sm text-gray-500">{t('pages.documentation.loading')}</p>
          ) : catsError ? (
            <p className="text-sm text-red-600">{catsError.message}</p>
          ) : sortedCategories.length === 0 ? (
            <p className="text-sm text-gray-500">{t('pages.documentation.noCategories')}</p>
          ) : (
            <div className="space-y-3">
              {sortedCategories.map((cat, index) => {
                const docs = documentsByCategory.get(cat.id) ?? [];
                const expanded = expandedDocCategoryId === cat.id;
                const name =
                  cat.locales.find((l) => l.locale === 'es')?.name ||
                  cat.locales[0]?.name ||
                  cat.slug;
                return (
                  <div
                    key={cat.id}
                    className="bg-white dark:bg-gray-800 shadow rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                          disabled={index === 0}
                          onClick={() => void moveCategory(index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                          disabled={index === sortedCategories.length - 1}
                          onClick={() => void moveCategory(index, 1)}
                        >
                          ↓
                        </button>
                      </div>
                      <button
                        type="button"
                        className="flex-1 min-w-[160px] text-left"
                        onClick={() =>
                          setExpandedDocCategoryId(expanded ? null : cat.id)
                        }
                      >
                        <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                          <span className="text-gray-400 text-xs">{expanded ? '▼' : '▶'}</span>
                          {name}
                          <span className="text-xs font-normal text-gray-400">
                            {t('pages.documentation.documentsCount', { count: docs.length })}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 font-mono ml-5">{cat.slug}</div>
                      </button>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          className="px-2.5 py-1.5 text-xs rounded-md bg-teal-600 text-white hover:bg-teal-700"
                          onClick={() =>
                            navigate(
                              `/documentation/articles/new?contentType=article&categoryId=${encodeURIComponent(cat.id)}`
                            )
                          }
                        >
                          + {t('pages.documentation.addDocument')}
                        </button>
                        <button
                          type="button"
                          className="text-teal-600 hover:underline text-sm"
                          onClick={() => openEditCategory(cat)}
                        >
                          {t('pages.documentation.edit')}
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:underline text-sm"
                          onClick={() => void handleDeleteCategory(cat.id)}
                        >
                          {t('pages.documentation.delete')}
                        </button>
                      </div>
                    </div>

                    {expanded && (
                      <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40">
                        {docs.length === 0 ? (
                          <div className="px-4 py-6 text-center space-y-2">
                            <p className="text-sm text-gray-500">
                              {t('pages.documentation.noDocumentsInCategory')}
                            </p>
                            <button
                              type="button"
                              className="text-sm font-medium text-teal-600 hover:underline"
                              onClick={() =>
                                navigate(
                                  `/documentation/articles/new?contentType=article&categoryId=${encodeURIComponent(cat.id)}`
                                )
                              }
                            >
                              + {t('pages.documentation.addDocument')}
                            </button>
                          </div>
                        ) : (
                          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                            {docs.map((doc) => (
                              <li
                                key={doc.slug}
                                className="px-4 py-3 flex flex-wrap items-center justify-between gap-2"
                              >
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {doc.title}
                                  </div>
                                  <div className="text-xs text-gray-400 font-mono">{doc.slug}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {t('pages.documentation.publishedLocalesShort', {
                                      published: doc.publishedLocales.length,
                                      total: doc.locales.length,
                                    })}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 text-sm whitespace-nowrap">
                                  <Link
                                    to={`/documentation/articles/${doc.editId}`}
                                    className="text-teal-600 hover:underline"
                                  >
                                    {t('pages.documentation.edit')}
                                  </Link>
                                  <button
                                    type="button"
                                    className="text-red-600 hover:underline"
                                    onClick={() => void handleDeleteArticleGroup(doc.ids)}
                                  >
                                    {t('pages.documentation.delete')}
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {catModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {catModal === 'new'
                ? t('pages.documentation.newCategory')
                : t('pages.documentation.editCategory')}
            </h3>
            <label className="block text-xs text-gray-500">
              Slug
              <input
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={catForm.slug}
                onChange={(e) => setCatForm((f) => ({ ...f, slug: e.target.value }))}
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs text-gray-500">
                {t('pages.documentation.columns.icon')}
                <select
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  value={catForm.icon}
                  onChange={(e) => setCatForm((f) => ({ ...f, icon: e.target.value }))}
                >
                  {ICONS.map((icon) => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-gray-500">
                {t('pages.documentation.columns.sort')}
                <input
                  type="number"
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  value={catForm.sortOrder}
                  onChange={(e) =>
                    setCatForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))
                  }
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={catForm.isActive}
                onChange={(e) => setCatForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              {t('pages.documentation.columns.active')}
            </label>
            {catForm.locales.map((loc, idx) => (
              <label key={loc.locale} className="block text-xs text-gray-500">
                {t('pages.documentation.columns.name')} ({loc.locale})
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  value={loc.name}
                  onChange={(e) => {
                    const locales = [...catForm.locales];
                    locales[idx] = { ...locales[idx], name: e.target.value };
                    setCatForm((f) => ({ ...f, locales }));
                  }}
                />
              </label>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setCatModal(null)}
                className="btn btn-sm btn-secondary"
              >
                {t('pages.documentation.cancel')}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSaveCategory()}
                className="btn btn-sm btn-primary"
              >
                {saving ? t('pages.documentation.saving') : t('pages.documentation.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
